/**
 * Studio Timeline dock — persistent footer-docked chrome (NOT a
 * swappable PanelManager panel). Mounted once in `bootStudio()` and
 * fixed to the bottom of the viewport so it survives every panel
 * switch.
 *
 * This is the studio's PRIMARY timeline surface: a pull-up drawer that
 * hosts the real, time-based `PremiereTimeline` editor (timeline.md /
 * mockups/timeline-premiere.png). The slim collapsed strip is the grab
 * handle + a one-line summary; expanding it reveals the full Premiere-
 * style cut (ruler, playhead, tracks, clips, zoom, export, score).
 *
 * Behaviour:
 *   - Default state is a slim strip with a drag handle + toggle.
 *   - Clicking the toggle (or pressing the handle) flips between the
 *     slim strip and the expanded editor.
 *   - Dragging the handle resizes the tray live; it can grow past half
 *     the viewport height and snaps back to the strip if dragged small.
 *
 * UI state (collapsed/expanded + last expanded height) is persisted to
 * localStorage so it survives reloads.
 */

import type { StudioStore, StudioStateKey } from "./StudioStore";
import type { PanelManager } from "./PanelManager";
import { PremiereTimeline } from "./PremiereTimeline";
import "./styles/timeline.css";

const UI_STATE_KEY = "koko_studio_timeline_ui";

/** Slim strip height (just the handle bar) when collapsed. */
const STRIP_HEIGHT = 44;
/** Default expanded height — clamped to the viewport at apply time. */
const DEFAULT_EXPANDED_HEIGHT = Math.round(window.innerHeight * 0.6);
/** Smallest expanded tray; below this a drag snaps back to collapsed. */
const MIN_EXPANDED_HEIGHT = 150;
/** Workspace kept visible above the drawer, adjusted for short screens. */
const MIN_WORKSPACE_HEIGHT = 240;
const MAX_WORKSPACE_RESERVE = 360;
/** Pointer travel (px) under which a handle press counts as a click. */
const DRAG_CLICK_SLOP = 4;

interface TimelineUiState {
  collapsed: boolean;
  expandedHeight: number;
}

/** Store slices the dock's summary strip reflects. */
const WATCHED_KEYS: StudioStateKey[] = [
  "scenes",
  "storyboardFramesBySceneId",
  "storyboardStatus",
  "videoStatus",
];

export class Timeline {
  private readonly store: StudioStore;
  private readonly onStateChange?: (expanded: boolean) => void;
  private readonly root: HTMLElement;
  private readonly toggleButton: HTMLButtonElement;
  private readonly countLabel: HTMLSpanElement;
  private readonly timeline: PremiereTimeline;
  private readonly resizeHandle: HTMLElement;

  private collapsed = true;
  private expandedHeight = DEFAULT_EXPANDED_HEIGHT;

  private storeUnsubscribers: (() => void)[] = [];

  // Drag bookkeeping.
  private dragging = false;
  private dragPointerId: number | null = null;
  private dragStartY = 0;
  private dragStartHeight = 0;
  private dragMoved = false;
  private readonly onViewportResize = (): void => this.applyState();

  constructor(options: {
    store: StudioStore;
    /** Enables click-to-edit jumps from a clip to its source tool. */
    panelManager?: PanelManager;
    /** Fired when the dock collapses / expands (keeps the sidebar toggle in sync). */
    onStateChange?: (expanded: boolean) => void;
  }) {
    this.store = options.store;
    this.onStateChange = options.onStateChange;

    this.loadUiState();

    this.root = document.createElement("section");
    this.root.className = "studio-timeline-dock";
    this.root.dataset.testid = "studio-timeline-dock";
    this.root.setAttribute("aria-label", "Film timeline");

    const handle = document.createElement("div");
    handle.className = "studio-timeline-dock-handle";
    handle.setAttribute("role", "separator");
    handle.setAttribute("aria-orientation", "horizontal");
    handle.setAttribute("aria-label", "Resize timeline height");
    handle.tabIndex = 0;
    this.resizeHandle = handle;

    this.toggleButton = document.createElement("button");
    this.toggleButton.type = "button";
    this.toggleButton.className = "studio-timeline-dock-toggle";
    this.toggleButton.setAttribute("aria-controls", "studio-timeline-body");

    const title = document.createElement("span");
    title.className = "studio-timeline-dock-title";
    title.textContent = "timeline";

    this.countLabel = document.createElement("span");
    this.countLabel.className = "studio-timeline-dock-count";

    handle.appendChild(this.toggleButton);
    handle.appendChild(title);
    handle.appendChild(this.countLabel);

    const body = document.createElement("div");
    body.className = "studio-timeline-dock-body";
    body.id = "studio-timeline-body";

    // The real, time-based Premiere editor lives inside the drawer.
    this.timeline = new PremiereTimeline({
      store: this.store,
      panelManager: options.panelManager,
      variant: "dock",
    });
    body.appendChild(this.timeline.getElement());

    this.root.appendChild(handle);
    this.root.appendChild(body);

    this.wireHandle(handle);
    this.toggleButton.addEventListener("click", () => this.toggle());

    window.addEventListener("resize", this.onViewportResize);
    this.applyState();
    this.renderSummary();
  }

  /** The element to mount into the page chrome. */
  getElement(): HTMLElement {
    return this.root;
  }

  /** Subscribe to the store. Call once after mounting. */
  start(): void {
    this.timeline.start();
    for (const key of WATCHED_KEYS) {
      this.storeUnsubscribers.push(
        this.store.subscribe(key, () => this.renderSummary()),
      );
    }
    this.renderSummary();
  }

  /** Tear down store subscriptions. */
  destroy(): void {
    window.removeEventListener("resize", this.onViewportResize);
    this.timeline.destroy();
    for (const unsub of this.storeUnsubscribers) unsub();
    this.storeUnsubscribers = [];
  }

  // ------------------------------------------------------------------
  // Collapse / expand + drag-resize
  // ------------------------------------------------------------------

  /** Collapse ⇄ expand the dock. Public so the sidebar toggle can drive it. */
  toggle(): void {
    this.collapsed = !this.collapsed;
    this.applyState();
    this.persistUiState();
    this.onStateChange?.(!this.collapsed);
  }

  /** Whether the dock is currently expanded into its scene tray. */
  isExpanded(): boolean {
    return !this.collapsed;
  }

  private wireHandle(handle: HTMLElement): void {
    // The toggle button has its own click handler; let it win.
    handle.addEventListener("pointerdown", (event) => {
      if (event.target === this.toggleButton) return;
      this.dragging = true;
      this.dragMoved = false;
      this.dragPointerId = event.pointerId;
      this.dragStartY = event.clientY;
      this.dragStartHeight = this.collapsed ? STRIP_HEIGHT : this.expandedHeight;
      handle.setPointerCapture(event.pointerId);
      this.root.classList.add("is-dragging");
    });

    handle.addEventListener("pointermove", (event) => {
      if (!this.dragging) return;
      const delta = this.dragStartY - event.clientY; // drag up → grow
      if (Math.abs(delta) > DRAG_CLICK_SLOP) this.dragMoved = true;
      const nextHeight = clamp(
        this.dragStartHeight + delta,
        STRIP_HEIGHT,
        this.maxHeight(),
      );
      // Live preview without committing collapsed/expanded semantics.
      this.root.style.height = `${nextHeight}px`;
    });

    const endDrag = (): void => {
      if (!this.dragging) return;
      this.dragging = false;
      this.root.classList.remove("is-dragging");
      if (this.dragPointerId !== null) {
        try {
          handle.releasePointerCapture(this.dragPointerId);
        } catch {
          // Pointer already released — ignore.
        }
      }
      this.dragPointerId = null;

      // A press that didn't really move is a toggle, not a resize.
      if (!this.dragMoved) {
        this.toggle();
        return;
      }

      const finalHeight = this.root.offsetHeight;
      if (finalHeight < MIN_EXPANDED_HEIGHT) {
        this.collapsed = true;
      } else {
        this.collapsed = false;
        this.expandedHeight = finalHeight;
      }
      this.applyState();
      this.persistUiState();
      this.onStateChange?.(!this.collapsed);
    };

    handle.addEventListener("pointerup", endDrag);
    handle.addEventListener("pointercancel", endDrag);
    handle.addEventListener("keydown", (event) => {
      if (event.target === this.toggleButton) return;
      let next: number | null = null;
      const current = this.collapsed ? MIN_EXPANDED_HEIGHT : this.root.offsetHeight;
      if (event.key === "ArrowUp") next = current + 24;
      if (event.key === "ArrowDown") next = current - 24;
      if (event.key === "Home") next = MIN_EXPANDED_HEIGHT;
      if (event.key === "End") next = this.maxHeight();
      if (next === null) return;
      event.preventDefault();
      this.collapsed = false;
      this.expandedHeight = clamp(next, MIN_EXPANDED_HEIGHT, this.maxHeight());
      this.applyState();
      this.persistUiState();
      this.onStateChange?.(true);
    });
  }

  /** Largest tray that still leaves useful panel workspace visible above. */
  private maxHeight(): number {
    const workspaceReserve = clamp(
      Math.round(window.innerHeight * 0.45),
      MIN_WORKSPACE_HEIGHT,
      MAX_WORKSPACE_RESERVE,
    );
    return Math.max(MIN_EXPANDED_HEIGHT, window.innerHeight - workspaceReserve);
  }

  private syncResizeAccessibility(height: number): void {
    this.resizeHandle.setAttribute(
      "aria-valuemin",
      String(this.collapsed ? STRIP_HEIGHT : MIN_EXPANDED_HEIGHT),
    );
    this.resizeHandle.setAttribute("aria-valuemax", String(this.maxHeight()));
    this.resizeHandle.setAttribute("aria-valuenow", String(height));
  }

  private applyState(): void {
    if (this.collapsed) {
      this.root.style.height = `${STRIP_HEIGHT}px`;
      this.root.dataset.state = "collapsed";
      this.toggleButton.textContent = "▲";
      this.toggleButton.setAttribute("aria-expanded", "false");
      this.toggleButton.setAttribute("aria-label", "Expand timeline");
      this.toggleButton.title = "Expand timeline";
      this.syncResizeAccessibility(STRIP_HEIGHT);
    } else {
      const height = clamp(
        this.expandedHeight,
        MIN_EXPANDED_HEIGHT,
        this.maxHeight(),
      );
      this.root.style.height = `${height}px`;
      this.root.dataset.state = "expanded";
      this.toggleButton.textContent = "▼";
      this.toggleButton.setAttribute("aria-expanded", "true");
      this.toggleButton.setAttribute("aria-label", "Collapse timeline");
      this.toggleButton.title = "Collapse timeline";
      this.syncResizeAccessibility(height);
      // Re-layout the editor now that the drawer has a real width/height.
      this.timeline.refresh();
    }
  }

  // ------------------------------------------------------------------
  // Rendering — the collapsed strip shows a one-line cut summary; the
  // expanded drawer hosts the full PremiereTimeline editor.
  // ------------------------------------------------------------------

  private renderSummary(): void {
    const { clipCount, durationSec } = this.timeline.getSummary();
    if (clipCount === 0) {
      this.countLabel.textContent = "empty";
      return;
    }
    const clips = clipCount === 1 ? "1 clip" : `${clipCount} clips`;
    this.countLabel.textContent = `${clips} · ${fmtTime(durationSec)}`;
  }

  // ------------------------------------------------------------------
  // Persistence
  // ------------------------------------------------------------------

  private loadUiState(): void {
    if (typeof window === "undefined" || !window.localStorage) return;
    const raw = window.localStorage.getItem(UI_STATE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as Partial<TimelineUiState>;
      if (typeof parsed.collapsed === "boolean") {
        this.collapsed = parsed.collapsed;
      }
      if (
        typeof parsed.expandedHeight === "number" &&
        Number.isFinite(parsed.expandedHeight)
      ) {
        this.expandedHeight = parsed.expandedHeight;
      }
    } catch {
      // Corrupt entry — fall back to defaults.
    }
  }

  private persistUiState(): void {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      const state: TimelineUiState = {
        collapsed: this.collapsed,
        expandedHeight: this.expandedHeight,
      };
      window.localStorage.setItem(UI_STATE_KEY, JSON.stringify(state));
    } catch {
      // Quota — silently ignore.
    }
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function fmtTime(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
}
